import apiV2 from '../api/v2';

interface CreateUploadResponse {
    fileId: string;
    userFileId: string;
    uploadUrl: string;
    headers: Record<string, string>;
}

interface CompleteUploadResponse {
    userFileId: string;
    profilePictureUrl: string;
}

/**
 * Uploads a profile picture using the three-step signed URL flow:
 * 1. Create upload (get signed URL)
 * 2. Upload to GCS
 * 3. Complete upload
 *
 * Returns the public profile picture URL.
 */
export async function uploadProfilePicture(
    file: File,
    onProgress?: (progress: number) => void
): Promise<string> {
    // Step 1: Create upload
    const createRes = await apiV2.post<{ success: boolean; data: CreateUploadResponse }>(
        '/user/profile-picture/upload',
        {
            originalFilename: file.name,
            mimeType: file.type || 'image/jpeg',
            sizeBytes: file.size
        }
    );
    const { userFileId, uploadUrl, headers } = createRes.data.data;

    // Step 2: Upload to GCS
    await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress((e.loaded / e.total) * 100);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                const errorText = xhr.responseText || xhr.statusText || 'Unknown error';
                reject(new Error(`Upload failed (${xhr.status}): ${errorText}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Network error occurred during upload'));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload was cancelled'));
        });

        xhr.open('PUT', uploadUrl);
        Object.entries({ ...headers, 'Content-Type': file.type || 'image/jpeg' }).forEach(
            ([key, value]) => xhr.setRequestHeader(key, value)
        );
        xhr.send(file);
    });

    // Step 3: Complete
    const completeRes = await apiV2.post<{ success: boolean; data: CompleteUploadResponse }>(
        `/user/profile-picture/${userFileId}/complete`
    );

    return completeRes.data.data.profilePictureUrl;
}
